/**
 * QuizVideoPlayer Component - hls.js based
 * Reimplemented based on reference_legacy VideoJsPlayer functionality
 * Uses hls.js instead of video.js for HLS playback
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import Hls from 'hls.js'

interface QuizVideoPlayerProps {
  /** Video source URL (HLS m3u8 or direct video URL) */
  src: string
  /** MIME type for the video */
  mimeType?: string
  /** Whether this is an HLS stream */
  isHls?: boolean
  /** Optional poster image */
  poster?: string
  /** Additional CSS classes */
  className?: string
  /** Ready callback */
  onReady?: () => void
  /** Error callback */
  onError?: (message: string) => void
}

/**
 * Video player component for quiz images that are videos
 * Uses hls.js for HLS playback with native fallback for Safari
 */
export function QuizVideoPlayer({
  src,
  mimeType,
  isHls = false,
  poster,
  className,
  onReady,
  onError,
}: QuizVideoPlayerProps) {
  const { t } = useTranslation('quiz')
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  // Detect Safari browser (includes iOS Safari)
  const isSafari = useMemo(() => {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent
    return (
      /^((?!chrome|android).)*safari/i.test(ua) ||
      (ua.includes('Mac') && ua.includes('Safari') && !ua.includes('Chrome'))
    )
  }, [])

  // Determine if source is HLS
  const isHlsSource = useMemo(() => {
    if (isHls) return true
    if (mimeType === 'application/x-mpegURL' || mimeType === 'application/vnd.apple.mpegurl') return true
    if (src.includes('.m3u8')) return true
    return false
  }, [isHls, mimeType, src])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !src) return

    const handleCanPlay = () => {
      setIsLoading(false)
      onReady?.()
    }

    const handleError = () => {
      setIsLoading(false)
      setHasError(true)
      onError?.(t('shared.cannotLoadVideo'))
    }

    const handleWaiting = () => {
      setIsLoading(true)
    }

    const handlePlaying = () => {
      setIsLoading(false)
    }

    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('playing', handlePlaying)

    // HLS playback setup
    if (isHlsSource) {
      // Check if browser has native HLS support (Safari)
      if (video.canPlayType('application/vnd.apple.mpegurl') || isSafari) {
        // Safari can play HLS natively
        video.src = src
      } else if (Hls.isSupported()) {
        // Use hls.js for other browsers
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
        })
        
        hls.loadSource(src)
        hls.attachMedia(video)
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false)
          onReady?.()
        })
        
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                // Try to recover from network error
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                // Try to recover from media error
                hls.recoverMediaError()
                break
              default:
                setHasError(true)
                onError?.(t('shared.cannotLoadVideo'))
                hls.destroy()
                break
            }
          }
        })
        
        hlsRef.current = hls
      } else {
        // HLS not supported
        setHasError(true)
        onError?.(t('shared.cannotLoadVideo'))
      }
    } else {
      // Non-HLS source (direct video URL)
      video.src = src
    }

    return () => {
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)
      video.removeEventListener('waiting', handleWaiting)
      video.removeEventListener('playing', handlePlaying)
      
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [src, isHlsSource, isSafari, onReady, onError])

  if (hasError) {
    return (
      <div className={cn('flex items-center justify-center py-8 bg-gray-100 rounded-xl', className)}>
        <p className="text-gray-500 text-sm">{t('shared.cannotLoadVideo')}</p>
      </div>
    )
  }

  return (
    <div className={cn('relative', className)}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl z-10">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}
      <video
        ref={videoRef}
        className="w-full rounded-xl"
        controls
        playsInline
        poster={poster}
        preload="auto"
      />
    </div>
  )
}
